import {
  DEFAULT_MIN_DPI,
  effectiveDpi,
  type PositionRequirement,
} from '@designdeck/shared';
import type { UIProduct } from '@/lib/product-transformer';
import type { StoredPositionLike } from './catalogFilter';

/**
 * Print resolution the design's images would reach on a product, after the
 * carry-over: a product-dependent fit (percent box, class change) can enlarge
 * a motif far beyond the width it was made at, and the card has to say so
 * before the seller picks the product.
 *
 * Pure so the geometry is tested on its own; the grid turns the result into
 * a chip.
 */

/** One stored image as the catalogue needs it; everything else is skipped. */
interface StoredImageLike {
  assetId?: unknown;
  width?: unknown;
  scaleX?: unknown;
  originalWidth?: unknown;
}

/** The part of an adapted placement this check reads. */
export interface AdaptedPlacementLike {
  placement: { relWidth: number };
}

export interface LowDpiWarning {
  slug: string;
  /** Lowest effective resolution among the images on that Position, rounded. */
  dpi: number;
  /** The minimum the Position's default method asks for. */
  minDpi: number;
}

function isPositive(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Lowest effective dpi of the uploaded images on one Position once the
 * design is carried onto `targetWidthMm` at `adaptedRelWidth`.
 *
 * Only objects with an asset origin count: an image rebuilt from a crop has
 * no natural size worth judging. `originalWidth` wins over the canvas bitmap
 * where a phone upload holds a reduced copy. Null when nothing can be judged.
 */
export function lowestImageDpi(
  objects: readonly unknown[],
  groupWidthPx: number,
  adaptedRelWidth: number,
  targetWidthMm: number
): number | null {
  if (!isPositive(groupWidthPx) || !isPositive(targetWidthMm)) return null;
  if (!isPositive(adaptedRelWidth)) return null;
  let lowest: number | null = null;
  for (const raw of objects) {
    const object = raw as StoredImageLike | null;
    if (!object || typeof object.assetId !== 'string') continue;
    if (!isPositive(object.width)) continue;
    const scaleX = isPositive(object.scaleX) ? object.scaleX : 1;
    const naturalWidthPx = isPositive(object.originalWidth)
      ? object.originalWidth
      : object.width;
    // Share of the design group this image covers, carried onto the target.
    const share = (object.width * scaleX) / groupWidthPx;
    const imageWidthMm = share * adaptedRelWidth * targetWidthMm;
    const dpi = effectiveDpi(naturalWidthPx, imageWidthMm);
    if (dpi === null) continue;
    if (lowest === null || dpi < lowest) lowest = dpi;
  }
  return lowest;
}

/** Minimum dpi the Position's default method asks for, else the house default. */
function minDpiOf(product: UIProduct, slug: string): number {
  const minDpi = product.matchPositions.find((p) => p.slug === slug)?.minDpi;
  return isPositive(minDpi) ? minDpi : DEFAULT_MIN_DPI;
}

/**
 * The Position whose images fall furthest below their minimum on this
 * product, or null when every image holds up (or nothing can be judged).
 * Embroidered Positions are stitched, not printed, and are skipped.
 */
export function lowDpiWarningFor(args: {
  product: UIProduct;
  requirements: readonly PositionRequirement[];
  masterPositions: Record<string, StoredPositionLike | undefined>;
  adaptations: ReadonlyMap<string, AdaptedPlacementLike> | null | undefined;
}): LowDpiWarning | null {
  const { product, requirements, masterPositions, adaptations } = args;
  if (!adaptations) return null;
  let worst: LowDpiWarning | null = null;
  for (const requirement of requirements) {
    if (requirement.decoration === 'embroidery') continue;
    const master = masterPositions[requirement.slug];
    const adaptation = adaptations.get(requirement.slug);
    const targetWidthMm = product.matchPositions.find(
      (p) => p.slug === requirement.slug
    )?.maxWidthMm;
    if (!master?.json?.objects || !master.printContext || !adaptation) continue;
    if (!isPositive(targetWidthMm)) continue;
    const groupWidthPx =
      (master.relativeState?.relWidth ?? 0) * master.printContext.widthPx;
    const dpi = lowestImageDpi(
      master.json.objects,
      groupWidthPx,
      adaptation.placement.relWidth,
      targetWidthMm
    );
    if (dpi === null) continue;
    const minDpi = minDpiOf(product, requirement.slug);
    if (dpi >= minDpi) continue;
    if (worst === null || dpi / minDpi < worst.dpi / worst.minDpi) {
      worst = { slug: requirement.slug, dpi: Math.round(dpi), minDpi };
    }
  }
  return worst;
}
